import { matchSorter } from 'match-sorter'
import React, { useEffect } from 'react'
import { OverlayTrigger, Pagination, Table, Tooltip } from 'react-bootstrap'
import { useTable, useFilters, Row, useExpanded, usePagination, useSortBy } from 'react-table'
import { getDateWithOffset } from '../Utility/GetDateWithOffset'

// Define a default UI for filtering
function DefaultColumnFilter({
    column: { filterValue, setFilter },
}: any) {
    return (
        <input
            className='pc-table-header-input'
            value={filterValue || ''}
            onChange={e => {
                setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
            }}
            placeholder={`Search...`}
        />
    )
}

// This is a custom filter UI for selecting
// a unique option from a list
export function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}: any) {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = React.useMemo(() => {
        const options = new Set()
        preFilteredRows.forEach((row: { values: { [x: string]: unknown } }) => {
            options.add(row.values[id])
        })
        return [...options.values() as any]
    }, [id, preFilteredRows])

    // Render a multi-select box
    return (
        <select
            className="pc-table-header-input pc-table-select-padding"
            value={filterValue}
            onChange={e => {
                setFilter(e.target.value || undefined)
            }}
        >
            <option value="">All</option>
            {options.sort((a: string, b: string) => a > b ? 1 : -1).map((option: string, i): any => (
                <option key={i} value={option}>
                    {option}
                </option>
            ))}
        </select>

    )
}

// This is a custom filter UI that uses a
// slider to set the filter value between a column's
// min and max values
export function SliderColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}: any) {
    // Calculate the min and max
    // using the preFilteredRows

    const [min, max] = React.useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        preFilteredRows.forEach((row: { values: { [x: string]: number } }) => {
            min = Math.min(row.values[id], min)
            max = Math.max(row.values[id], max)
        })
        return [min, max]
    }, [id, preFilteredRows])

    return (
        <>
            <input
                type="range"
                min={min}
                max={max}
                value={filterValue || min}
                onChange={e => {
                    setFilter(parseInt(e.target.value, 10))
                }}
            />
            <button onClick={() => setFilter(undefined)}>Off</button>
        </>
    )
}

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
export function NumberRangeColumnFilter({
    column: { filterValue, preFilteredRows, setFilter, id },
}: any) {
    const [min, max] = React.useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        preFilteredRows.forEach((row: { values: { [x: string]: number } }) => {
            min = Math.min(row.values[id], min)
            max = Math.max(row.values[id], max)
        })
        return [min, max]
    }, [id, preFilteredRows])

    return (
        <div
            style={{
                display: 'flex',
            }}
        >
            <input
                value={filterValue[0] || ''}
                type="number"
                onChange={e => {
                    const val = e.target.value
                    setFilter((old: any[]) => [val ? parseInt(val, 10) : undefined, old[1]])
                }}
                placeholder={`Min (${min})`}
                style={{
                    width: '70px',
                    marginRight: '0.5rem',
                }}
            />
            to
            <input
                value={filterValue[1] || ''}
                type="number"
                onChange={e => {
                    const val = e.target.value
                    setFilter((old: any[]) => [old[0], val ? parseInt(val, 10) : undefined])
                }}
                placeholder={`Max (${max})`}
                style={{
                    width: '70px',
                    marginLeft: '0.5rem',
                }}
            />
        </div>
    )
}
function formatDate(dateValue: number) {
    var date = dateValue ? new Date(dateValue).toString() : new Date(0, 0).toString();
    return getDateWithOffset(date).toLocaleDateString();
}
function fuzzyDateFilterFn(rows: any, id: number, filterDateValue: string) {
    return matchSorter(rows, filterDateValue, {
        keys: [(row: any) => formatDate(row.values[id])]
    });
}

function fuzzyTextFilterFn(rows: any, id: number, filterValue: string) {
    return matchSorter(rows, filterValue, {
        keys: [(row: any) => row.values[id]]
    });
}


// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val: any) => !val
fuzzyDateFilterFn.autoRemove = (val: any) => !val;

// Our table component
export function PreCertTable({ columns, defaultFilters, data, defaultPageSize, compact, renderRowSubComponent, isRowExpanded, hidePagination, onFiltersChanged, getRowProps = () => ({}) }: any) {
    const filterTypes = React.useMemo(
        () => ({
            // Add a new fuzzyTextFilterFn filter type.
            fuzzyText: fuzzyTextFilterFn,
            fuzzyDate: fuzzyDateFilterFn,
            // Or, override the default text filter to use
            // "startWith"
            text: (rows: Array<Row<any>>, id: any, filterValue: any) => {
                return rows.filter(row => {
                    const rowValue = row.values[id]
                    return rowValue !== undefined
                        ? String(rowValue)
                            ?.toLowerCase()
                            .startsWith(String(filterValue)?.toLowerCase())
                        : true
                })
            },
        }),
        []
    )

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        []
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        pageCount,
        gotoPage,
        previousPage,
        nextPage,
        state: { pageIndex, pageSize, filters },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            filterTypes,
            autoResetPage: false,
            autoResetFilters: false,
            initialState: { pageIndex: 0, pageSize: defaultPageSize, filters: defaultFilters ?? [] },
            sortTypes: {
                alphanumeric: (row1: Row<any>, row2: Row<any>, columnName: string) => {
                    const rowOneColumn = row1.values[columnName];
                    const rowTwoColumn = row2.values[columnName];
                    if (isNaN(rowOneColumn)) {
                        return rowOneColumn?.toUpperCase() >
                            rowTwoColumn?.toUpperCase()
                            ? 1
                            : -1;
                    }
                    return Number(rowOneColumn) > Number(rowTwoColumn) ? 1 : -1;
                }
            }
        } as any,
        useFilters,
        useSortBy,
        useExpanded,
        usePagination
    ) as any;

    // We don't want to render all of the rows for this example, so cap
    // it for this use case
    const paddingStyle = compact ? { paddingTop: "3px", paddingBottom: "3px" } : null;
    var containsFilter = headerGroups[0].headers
        .map((_: any) => _.filterable)
        .includes(true);

    var pager = null;
    if (pageSize && !hidePagination) {
        var pageNumbers: Array<number> = null;
        var maxPageNumbers = defaultPageSize ?? 20;
        var startIndex = pageIndex - (pageIndex % maxPageNumbers);
        pageNumbers = Array.from(Array(pageCount > maxPageNumbers ? maxPageNumbers : pageCount), (_, i) => startIndex + i + 1)
        pager = (<div className="d-flex justify-content-center">
            <Pagination>
                <Pagination.Prev onClick={() => previousPage()} />
                {pageNumbers.map(pageNumber => <Pagination.Item key={pageNumber} active={pageIndex + 1 === pageNumber} onClick={() => gotoPage(pageNumber - 1)}>{pageNumber}</Pagination.Item>)}
                <Pagination.Next onClick={() => nextPage()} />
            </Pagination>
        </div>);
    }

    var getCurrentRows = () => {
        return page?.length === 0 ? rows : page;
    }

    useEffect(() => {
        if (onFiltersChanged) {
            onFiltersChanged(rows, filters)
        }
    }, [rows,filters]);

    return (
        <>
            <Table hover striped borderless variant='pc-secondary-grey' {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup: any) => (
                        <tr
                            {...headerGroup.getHeaderGroupProps()}
                        >
                            {headerGroup.headers.map((column: any) => (
                                <th
                                    key={column.id}
                                    style={{ width: column.width, maxWidth: column.width, ...paddingStyle }}
                                >
                                    <span
                                        {...column.getHeaderProps(
                                            column.sortable ? column.getSortByToggleProps() : ""
                                        )}
                                    >
                                        <span>
                                            {column.render("Header")}
                                            {column.HeaderInfo && column.HeaderInfo.length > 0 &&
                                            <React.Fragment>
                                                <OverlayTrigger placement={"left-end"} overlay={<Tooltip id="PgyInfo" bsPrefix="pc-tooltip-eligibility">{column.HeaderInfo}</Tooltip>}>
                                                    <span>&nbsp;<i className="las la-info-circle ml-1 text-pp-secondary"></i></span>
                                                </OverlayTrigger>
                                            </React.Fragment>}
                                        </span>
                                        {column.isSorted ? (
                                            column.isSortedDesc ? (
                                                <i className="las la-arrow-down text-primary pc-font-size-largest "></i>
                                            ) : (
                                                <i className="las la-arrow-up text-primary pc-font-size-largest "></i>
                                            )
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    {containsFilter
                        ? headerGroups.map((headerGroup: any) => (
                            <tr
                                {...headerGroup.getHeaderGroupProps()}
                            >
                                {headerGroup.headers.map((column: any) => (
                                    <th
                                        key={column.id}
                                        style={{ width: column.width, maxWidth: column.width, ...paddingStyle }}
                                    >
                                        <span>
                                            {column.filterable ? column.render("Filter") : null}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))
                        : null}

                </thead>
                <tbody {...getTableBodyProps()}>
                    {getCurrentRows().map((row: any) => {
                        prepareRow(row);
                        row.isExpanded = (isRowExpanded) ? isRowExpanded(row) : false;
                        return (
                            <React.Fragment key={row.id}>
                                <tr {...row.getRowProps(getRowProps(row))} key={row.id}>
                                    {row.cells.map((cell: any) => {
                                        return (
                                            <td
                                                style={{ width: cell.column.width, maxWidth: cell.column.width, ...paddingStyle, ...cell.column.style }}
                                                {...cell.getCellProps()}
                                            >
                                                <span style={{ overflow: "visible" }}>{cell.render("Cell")}</span>
                                            </td>
                                        );
                                    })}
                                </tr>
                                {row.isExpanded ? renderRowSubComponent({ row }) : null}
                            </React.Fragment>
                        );
                    })}
                </tbody>

            </Table>
            <br />
            {pager}
        </>
    )
}

// Define a custom filter filter function!
function filterGreaterThan(rows: any[], id: string | number, filterValue: number) {
    return rows.filter(row => {
        const rowValue = row.values[id]
        return rowValue >= filterValue
    })
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = (val: any) => typeof val !== 'number'


